/**
 * Colour measurement step of an iteration.
 *
 * Every patch group of the iteration gets a main photo and a normalization
 * photo; Next then measures all the groups that still need it in one go, rather
 * than once per group, so a run is not lost to a half-measured iteration.
 *
 * Corner points of each plate photo are confirmed here - the measurement
 * rectifies the photo from them, so a wrong corner is a wrong colour for every
 * patch on that plate.
 */
import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Stack,
    Button,
    Modal,
    Box as MuiBox,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Paper,
    CircularProgress,
    LinearProgress,
    Box,
    Alert,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import NavBarBottom, { navBottomButtonSx } from "../../components/NavBarBottom";
import { useExplorationParams } from "../../helper/entityRoutes";
import { EXPLORATION_LIST_ROUTE } from "../../helper/explorationRoutes";
import useIterationState from "../../helper/useIterationState";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import IterationExitDialog from "../../components/IterationExitDialog";
import LaserMarkPanel from "../../components/LaserMarkPanel";
import MarkingOptionsBox from "../../components/MarkingOptionsBox";
import { markingTimeFromHeaders } from "../../helper/markingTime";
import CameraCapturePanel from "../../components/CameraCapturePanel";
import LoadComponent from "../../components/LoadComponent";
import PreviewIcon from "@mui/icons-material/Preview";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import ReplayIcon from "@mui/icons-material/Replay";
import axios from "axios";
import { naturalToDisplayedImageCoords, displayedToNaturalImageCoords } from "../../helper/imageOverlayCoordinates";
import { openMarkingPreviewWindow, getFilenameFromContentDisposition } from "../../helper/markingPreviewWindow";
import {
    EXPLORATION_FLOW_STEPS,
    explorationIdLast4,
    populationCodeForPhase,
    populationLabelForPhase,
} from "../../helper/colorExplorationParameters";
import {
    revertIterationColorMeasurement,
    restartIteration,
} from "../../helper/iterationFlowControls";

const actionButtonSx = {
    textTransform: "none",
    borderRadius: 2,
    px: 2.5,
    py: 1,
    fontWeight: 600,
    boxShadow: "none",
    "&:hover": { boxShadow: 1 },
};

const stepCardSx = {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    m: 0,
    boxSizing: "border-box",
    p: { xs: 2, sm: 2.5 },
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "background.paper",
};

/** Numbered step badge — matches Geometric Measurement step cards. */
const stepNumberCircleSx = {
    minWidth: 28,
    height: 28,
    borderRadius: "50%",
    bgcolor: "primary.main",
    color: "primary.contrastText",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    typography: "subtitle2",
    fontWeight: 700,
};

function emptyColorBatchSlot() {
    return {
        cameraColorImage: "",
        cameraColorImageData: null,
        /** name of the file already stored on the server by api/exploration/photo (one round-trip) */
        uploadedFileName: null,
        cornerPoints: [],
        fileExtensionColorImage: "",
        scalingFactors: null,
        colorImageBusy: false,
        colorPreviewMessage: "",
        /** Batch already color-measured on the server (resume / refresh). */
        serverComplete: false,
    };
}

export default function ColorExplorationColorColor() {
    const [colorSlots, setColorSlots] = useState([]);
    const colorSlotsRef = useRef(colorSlots);
    colorSlotsRef.current = colorSlots;
    const slotImageRefs = useRef([]);

    /**
     * Normalization (flat-field) image. It is the same reference shot for every
     * patch group and every iteration, so instead of re-selecting it per slot
     * we keep a single page-level one and persist it server-side as the
     * exploration default. On later iterations the page detects the stored
     * default and reuses it unless the user explicitly replaces it.
     */
    const [sharedNormFile, setSharedNormFile] = useState(null);
    const [sharedNormExt, setSharedNormExt] = useState("");
    const [sharedNormPreview, setSharedNormPreview] = useState("");
    const [sharedNormBusy, setSharedNormBusy] = useState(false);
    const [sharedNormPreviewMessage, setSharedNormPreviewMessage] = useState("");
    /** A default normalization image already exists server-side for this exploration. */
    const [sharedNormOnServer, setSharedNormOnServer] = useState(false);
    const sharedNormFileRef = useRef(null);
    sharedNormFileRef.current = sharedNormFile;
    const sharedNormExtRef = useRef("");
    sharedNormExtRef.current = sharedNormExt;
    const sharedNormOnServerRef = useRef(false);
    sharedNormOnServerRef.current = sharedNormOnServer;
    /** Whether the current fresh file has already been pushed to the server this run. */
    const sharedNormUploadedRef = useRef(false);

    const modalImageRef = useRef(null);
    /** Which color batch slot is open in the corner modal (null = closed). */
    const [modalSlotIndex, setModalSlotIndex] = useState(null);
    /** Bumped after modal image loads / resizes so corner overlays use correct layout (object-fit: contain). */
    const [modalImageLayoutTick, setModalImageLayoutTick] = useState(0);
    const [, setCameraThumbLayoutTick] = useState(0);
    const [automaticCornerDetectionBusy, setAutomaticCornerDetectionBusy] = useState(false);
    const [measureColorBusy, setMeasureColorBusy] = useState(false);
    const [downloadMarkingLoading, setDownloadMarkingLoading] = useState(false);
    const [previewMarkingLoading, setPreviewMarkingLoading] = useState(false);
    const [totalBatches, setTotalBatches] = useState(0);
    const [currentPatchIndex, setCurrentPatchIndex] = useState(0);
    const failedColorBatchRef = useRef(0);
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    /** Server state: every color batch of this iteration is measured (api/patch/index/detail). */
    const [measuredAll, setMeasuredAll] = useState(false);
    const [showWrongMeasurementData, setShowWrongMeasurementData] = useState(false);
    const [uploadValidationError, setUploadValidationError] = useState(null);
    /**
     * Generic server-side error from the color upload + measure pipeline.
     * Kept distinct from `uploadValidationError`/`showWrongMeasurementData`
     * which is reserved for OCR / [iteration][batch] mismatches with a
     * "Continue anyway" recovery — bundling all errors into that dialog
     * was misleading the user when measure_color crashed for unrelated
     * reasons (e.g. backend precondition failure or a 504 timeout).
     */
    const [colorMeasureError, setColorMeasureError] = useState(null);
    const [colorMeasureRecoveryBusy, setColorMeasureRecoveryBusy] = useState(false);
    /**
     * Cancel dialog. Replaces the silent Cancel that left a half-measured
     * iteration in the DB without any prompt or recovery option.
     */
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    /**
     * "Restart iteration" recovery dialog. The Color page is the end of
     * the line: by the time the user gets here the cluster step is
     * already finished server-side, the home screen routes the exploration
     * here, and (until now) there was no UI affordance to walk back to
     * Geometric/Width if the cluster image turned out to be wrong.
     */
    const [restartClusterDialogOpen, setRestartClusterDialogOpen] = useState(false);
    const [restartClusterBusy, setRestartClusterBusy] = useState(false);
    const [restartClusterError, setRestartClusterError] = useState(null);
    // The exploration is the URL's; the running iteration is fetched for it
    // (the sidebar shows the user-facing number: P_0 and Q_0 both belong to
    // iteration 0, only Q_n bumps it to n; the backend is keyed by the
    // internal number).
    const { explorationId, stepRoute } = useExplorationParams();
    const { iteration, iterationRef, applyIterationResponse } = useIterationState();
    const explorationIteration = iteration.display;
    const iterationPhase = iteration.phase;

    const navigate = useNavigate();

    // A manual start without the optimizer (exploration.manual_start) ends
    // the exploration once P0 is measured: Next then completes it instead of
    // preparing a next iteration, and waits for the backend to say so.
    const [explorationRow, setExplorationRow] = useState(null);
    const [completingBusy, setCompletingBusy] = useState(false);
    const [completeError, setCompleteError] = useState(null);
    useEffect(() => {
        if (!explorationId) return undefined;
        let cancelled = false;
        axios
            .get(`api/color/explorer/exploration/${explorationId}/`)
            .then((response) => {
                if (!cancelled) setExplorationRow(response?.data || null);
            })
            .catch((error) => {
                console.error("Error loading the exploration:", error);
            });
        return () => {
            cancelled = true;
        };
    }, [explorationId]);
    const completesAfterP0 =
        explorationRow?.manual_start?.continue_with_optimizer === false &&
        iterationPhase === "P";

    const updateColorSlot = (batchIndex, partial) => {
        setColorSlots((prev) => {
            const next = [...prev];
            const cur = next[batchIndex] ?? emptyColorBatchSlot();
            next[batchIndex] = { ...cur, ...partial };
            return next;
        });
    };

    useEffect(() => {
        const n = Math.max(0, Number(totalBatches) || 0);
        if (n <= 0) return;
        const idx = Math.max(0, Number(currentPatchIndex) || 0);
        setColorSlots((prev) =>
            Array.from({ length: n }, (_, i) => {
                const base = prev[i] ?? emptyColorBatchSlot();
                const serverComplete = measuredAll ? true : i < idx;
                return { ...base, serverComplete };
            })
        );
    }, [totalBatches, currentPatchIndex, measuredAll]);

    const getPatchDetails = async () => {
        const request_data = {
            "explorationId": explorationId,
            "iterationNumber": iterationRef.current.number,
            "isWidthPatchGroup": false,
        };
        try {
            const response = await axios.post("api/patch/index/detail", request_data);
            if (response.data?.needsWidthReviewBeforeColor) {
                navigate(stepRoute("width"), { replace: true });
                return null;
            }
            setTotalBatches(response.data.totalBatchCount);
            setCurrentPatchIndex(response.data.currentIndex);
            setMeasuredAll(response.data.measured);
            return response.data;
        } catch (error) {
            console.error("Error:", error);
            return null;
        }
    }

    // Predicted marking time of this iteration's colour marking file, asked
    // for as soon as the iteration is known (timeOnly builds no file; before
    // the width review the file cannot be built and nothing is shown).
    const [markingTime, setMarkingTime] = useState(null);
    const fetchMarkingTime = async () => {
        try {
            const response = await axios.post("api/download/marking/file", {
                explorationId,
                patchType: "FULL_CLUSTER",
                iterationNumber: iterationRef.current.number,
                timeOnly: true,
            });
            setMarkingTime(response.data?.marking_time || null);
        } catch (error) {
            setMarkingTime(null);
        }
    };
    const getIterationNumber = async () => {
        const request_data = { "explorationId": explorationId };
        try {
            const response = await axios.post("api/exploration/iteration/number", request_data);
            applyIterationResponse(response);
            getPatchDetails();
            fetchMarkingTime();
        } catch (error) {
            console.error("Error fetching iteration number:", error);
        }
    };

    const handleImageClick = (event) => {
        const image = modalImageRef.current;
        if (!image || modalSlotIndex == null) return;
        const imageRect = image.getBoundingClientRect();
        const x = event.clientX - imageRect.left;
        const y = event.clientY - imageRect.top;

        const { x: x_a, y: y_a } = displayedToNaturalImageCoords(image, x, y);

        const newCoordinates = { x_a, y_a };
        const slot = colorSlotsRef.current[modalSlotIndex];
        const prevCorners = slot?.cornerPoints ?? [];
        if (prevCorners.length >= 4) return;
        const cornerPoints = [...prevCorners, newCoordinates];
        updateColorSlot(modalSlotIndex, { cornerPoints });
    };

    const handleClearCornerPoints = () => {
        if (modalSlotIndex == null) return;
        updateColorSlot(modalSlotIndex, { cornerPoints: [] });
    };

    const handleCloseErrorDialog = () => {
        setShowWrongMeasurementData(false);
        setUploadValidationError(null);
    };

    const handleContinueWithoutIdentifierCheckColor = async () => {
        setShowWrongMeasurementData(false);
        setUploadValidationError(null);
        const start = failedColorBatchRef.current;
        setMeasureColorBusy(true);
        try {
            const declined = colorSlotsRef.current[start];
            if (declined?.cameraColorImageData && declined.uploadedFileName !== declined.cameraColorImageData.name) {
                // the photo endpoint declined the identifier: store it again with the check skipped
                await storePlatePhotoForSlot(start, declined.cameraColorImageData, declined.fileExtensionColorImage, true);
            }
            const okFirst = await measureColorForBatch(start, { skipIdentifierValidation: true });
            if (!okFirst) {
                return;
            }
            const slots = colorSlotsRef.current;
            for (let i = start + 1; i < slots.length; i++) {
                if (slots[i]?.serverComplete) continue;
                const ok = await measureColorForBatch(i, { skipIdentifierValidation: false });
                if (!ok) {
                    return;
                }
            }
            let allMeasured = false;
            try {
                const statusResponse = await axios.post("api/patch/index/detail", {
                    explorationId: explorationId,
                    iterationNumber: iterationRef.current.number,
                    isWidthPatchGroup: false,
                });
                allMeasured = Boolean(statusResponse?.data?.measured);
                setMeasuredAll(allMeasured);
            } catch (error) {
                console.error("Error refreshing color patch details after bypass measure:", error);
            }
            if (allMeasured) {
                openResults(stepRoute("summary"));
            }
        } finally {
            setMeasureColorBusy(false);
        }
    };

    const handleAutomaticCornerDetection = async () => {
        if (modalSlotIndex == null) return;
        const slot = colorSlotsRef.current[modalSlotIndex];
        const file = slot?.cameraColorImageData;
        const ext = slot?.fileExtensionColorImage;
        if (!file || !(file instanceof File) || !ext) return;
        let xScale = null;
        let yScale = null;
        if (slot.scalingFactors && typeof slot.scalingFactors === "object") {
            xScale = parseFloat(slot.scalingFactors.xScale);
            yScale = parseFloat(slot.scalingFactors.yScale);
            if (Number.isNaN(xScale)) xScale = null;
            if (Number.isNaN(yScale)) yScale = null;
        }
        setAutomaticCornerDetectionBusy(true);
        try {
            if (slot.uploadedFileName !== file.name) {
                throw new Error("The plate photo is not stored on the server; pick the file again.");
            }
            // the photo is on the server already: re-detect there, nothing travels
            const res = await axios.post("api/exploration/photo/corners", {
                explorationId: explorationId,
                fileName: `MainImg_${modalSlotIndex}.${ext}`,
                batch: modalSlotIndex,
            }, { timeout: 600000 });
            const corners = (res.data?.cornerPoints || []).map((point) => ({
                x_a: point[0] / (xScale || 1), y_a: point[1] / (yScale || 1),
            }));
            updateColorSlot(modalSlotIndex, { cornerPoints: corners });
        } catch (error) {
            console.error("Automatic corner detection failed:", error);
        } finally {
            setAutomaticCornerDetectionBusy(false);
        }
    };

    const handleCameraColorImageChangeForSlot = async (batchIndex, event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        await processCameraColorFileForSlot(batchIndex, file);
    };

    // Shared by the file picker and the camera-agent capture panel: a main
    // plate photo arrives as a File either way and flows through the same
    // conversion + corner detection.
    /**
     * ONE round-trip: api/exploration/photo stores the RAW at its final
     * location (the same MainImg_<batch> the measurement reads), detects the
     * corner crosses on the stored file and answers with the preview. Returns
     * false when the endpoint declined (e.g. identifier check), so the caller
     * shows the reason; "continue anyway" stores it again with the check skipped.
     */
    const storePlatePhotoForSlot = async (batchIndex, file, fileExtension, skipIdentifierValidation = false) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("explorationId", explorationId);
        formData.append("fileName", `MainImg_${batchIndex}.${fileExtension}`);
        formData.append("batch", String(batchIndex));
        if (skipIdentifierValidation) formData.append("skipIdentifierValidation", "true");
        const response = await axios.post("api/exploration/photo", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 900000,
        });
        if (!response.data?.Success || !response.data.image) {
            if (response.data && response.data.Success === false && response.data.Expected != null) {
                // the identifier check declined the photo: the Attention dialog takes over
                failedColorBatchRef.current = batchIndex;
                setUploadValidationError(response.data);
                setShowWrongMeasurementData(true);
                updateColorSlot(batchIndex, { colorPreviewMessage: "" });
            } else {
                updateColorSlot(batchIndex, { colorPreviewMessage: "The photo could not be stored; pick the file again." });
            }
            return false;
        }
        const { image, scaling_factors, cornerPoints } = response.data;
        const binaryImage = new Uint8Array(image.split("").map((char) => char.charCodeAt(0)));
        const blobUrl = URL.createObjectURL(new Blob([binaryImage], { type: "image/png" }));
        const xScale = parseFloat(scaling_factors.x_scale);
        const yScale = parseFloat(scaling_factors.y_scale);
        updateColorSlot(batchIndex, {
            cameraColorImage: blobUrl,
            scalingFactors: { xScale, yScale },
            cornerPoints: (cornerPoints || []).map((point) => ({ x_a: point[0] / xScale, y_a: point[1] / yScale })),
            uploadedFileName: file.name,
            colorPreviewMessage: "",
        });
        return true;
    };

    /**
     * A picked plate photo replaces the slot. `colorPreviewMessage` is the
     * busy text while uploading and, once idle, the reason the slot is still
     * empty; only a successful store clears it.
     */
    const processCameraColorFileForSlot = async (batchIndex, file) => {
        const fileName = file.name;
        const fileExtension = fileName.split(".").pop() || "";
        const extUpper = fileExtension.toUpperCase();

        if (extUpper !== "ARW" && extUpper !== "NEF") {
            console.error("Unsupported extension for raw conversion:", fileExtension);
            updateColorSlot(batchIndex, {
                ...emptyColorBatchSlot(),
                colorPreviewMessage: "Only ARW or NEF photos are supported.",
            });
            return;
        }

        updateColorSlot(batchIndex, {
            ...emptyColorBatchSlot(),
            colorImageBusy: true,
            colorPreviewMessage: "Uploading and detecting corner points…",
            fileExtensionColorImage: fileExtension,
            cameraColorImageData: file,
        });

        try {
            // ONE round-trip: api/exploration/photo stores the RAW, detects the
            // corners and answers with the preview. There is no other path.
            // storePlatePhotoForSlot sets the slot message for every outcome.
            await storePlatePhotoForSlot(batchIndex, file, fileExtension);
        } catch (error) {
            console.error("Error processing camera image:", error);
            updateColorSlot(batchIndex, { colorPreviewMessage: "The photo could not be stored; pick the file again." });
        } finally {
            updateColorSlot(batchIndex, { colorImageBusy: false });
        }
    };

    /**
     * Select the single page-level normalization image, shared by every patch
     * group. Converts to a PNG preview the same way the main image does. The
     * raw file is only pushed to the server (as the exploration default) at
     * measure time, so picking it here is cheap and re-pickable.
     */
    const handleSharedNormImageChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        await processSharedNormFile(file);
    };

    // Shared by the file picker and the camera-agent capture panel.
    const processSharedNormFile = async (file) => {
        setSharedNormBusy(true);
        setSharedNormPreviewMessage("Uploading and converting image…");

        const fileExtension = file.name.split(".").pop() || "";
        const extUpper = fileExtension.toUpperCase();
        setSharedNormFile(file);
        setSharedNormExt(fileExtension);
        // A freshly picked file must be (re)uploaded before the next measure.
        sharedNormUploadedRef.current = false;

        try {
            if (extUpper !== "ARW" && extUpper !== "NEF") {
                console.error("Unsupported extension for raw conversion:", fileExtension);
                setSharedNormPreview(URL.createObjectURL(file));
                return;
            }

            // ONE round-trip: store it as the exploration default right away;
            // the endpoint answers with the preview.
            try {
                const stored = await uploadSharedNormFile(file, fileExtension);
                if (stored?.Success) {
                    sharedNormUploadedRef.current = true;
                    setSharedNormOnServer(true);
                    if (stored.preview) {
                        setSharedNormPreview(`data:image/png;base64,${stored.preview}`);
                        return;
                    }
                }
            } catch (storeError) {
                console.error("Storing the normalization image failed; falling back to convert:", storeError);
            }

            const formData = new FormData();
            formData.append("tiff_file", file);
            formData.append("fileExtension", fileExtension);

            const response = await axios.post("api/convert-to-png/", formData, { responseType: "json" });
            if (!response.data) {
                console.error("Invalid response format");
                return;
            }

            const { image } = response.data;
            const binaryImage = new Uint8Array(image.split("").map((char) => char.charCodeAt(0)));
            const blob = new Blob([binaryImage], { type: "image/png" });
            setSharedNormPreview(URL.createObjectURL(blob));
        } catch (error) {
            console.error("Error converting normalization image:", error);
        } finally {
            setSharedNormBusy(false);
            setSharedNormPreviewMessage("");
        }
    };

    /**
     * Ensure the exploration has a default normalization image on the server
     * before measuring. Idempotent within a run: a freshly picked file is
     * uploaded once; if no fresh file was picked we rely on a previously
     * stored default.
     * @returns {Promise<boolean>} true if a usable normalization image is available server-side.
     */
    const uploadSharedNormFile = async (file, fileExtension) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("explorationId", explorationId);
        formData.append("fileExtension", fileExtension);
        const res = await axios.post("api/normalization/default", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 600000,
        });
        return res.data;
    };

    const ensureSharedNormUploaded = async () => {
        if (sharedNormUploadedRef.current) return true;
        const file = sharedNormFileRef.current;
        if (!file) {
            // No fresh selection this session: a stored default must exist.
            return sharedNormOnServerRef.current;
        }
        const res = { data: await uploadSharedNormFile(file, sharedNormExtRef.current) };
        if (res.data?.Success) {
            sharedNormUploadedRef.current = true;
            setSharedNormOnServer(true);
            return true;
        }
        return false;
    };

    /**
     * Upload main + norm images and call `api/measure/color` for one batch.
     * @returns {Promise<boolean>} false if main upload validation failed (dialog shown)
     */
    const measureColorForBatch = async (batchIndex, options = {}) => {
        const skipIdentifierValidation = options.skipIdentifierValidation === true;
        const slot = colorSlotsRef.current[batchIndex];
        if (!slot || slot.serverComplete) {
            return true;
        }
        if (
            !slot.cameraColorImage ||
            !slot.cameraColorImageData ||
            !slot.scalingFactors ||
            slot.cornerPoints.length !== 4
        ) {
            return false;
        }

        // The normalization image is shared across all patch groups; make sure
        // the exploration default exists server-side before measuring.
        let normReady = false;
        try {
            normReady = await ensureSharedNormUploaded();
        } catch (normError) {
            console.error("Error uploading normalization image:", normError);
        }
        if (!normReady) {
            failedColorBatchRef.current = batchIndex;
            setColorMeasureError({
                flavour: "precondition",
                status: null,
                code: "MISSING_NORMALIZATION_IMAGE",
                message:
                    "No normalization image is available. Choose a normalization image before measuring.",
                batchIndex,
            });
            return false;
        }

        // Already stored by api/exploration/photo when it was picked: nothing
        // to send but the corner points. Otherwise the legacy upload.
        const alreadyStored = Boolean(slot.uploadedFileName)
            && slot.uploadedFileName === slot.cameraColorImageData?.name;
        const formData = new FormData();
        formData.append("file", slot.cameraColorImageData);
        formData.append("explorationId", explorationId);
        formData.append("fileName", `MainImg_${batchIndex}.${slot.fileExtensionColorImage}`);
        formData.append("batch", String(batchIndex));
        if (skipIdentifierValidation) {
            formData.append("skipIdentifierValidation", "true");
            formData.append("skipIdentifierValidation", "1");
        }

        try {
            if (!alreadyStored) {
                const uploadMainRes = await axios.post("api/upload/file-to-cloud", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                if (!uploadMainRes.data?.Success) {
                    failedColorBatchRef.current = batchIndex;
                    setUploadValidationError(uploadMainRes.data ?? null);
                    setShowWrongMeasurementData(true);
                    return false;
                }
            }

            const xScale = slot.scalingFactors.xScale;
            const yScale = slot.scalingFactors.yScale;
            const resizedCornerPoints = slot.cornerPoints.map((point) => ({
                x_a: point.x_a * xScale,
                y_a: point.y_a * yScale,
            }));

            const request_data = {
                explorationId: explorationId,
                cornerPoints: resizedCornerPoints.map((point) => [point.x_a, point.y_a]),
                batchNumber: batchIndex,
            };

            await axios.post("api/measure/color", request_data);
            await getPatchDetails();
            return true;
        } catch (error) {
            console.error("Error during upload or color measure:", error);
            const status = error?.response?.status ?? null;
            const responseData = error?.response?.data ?? null;
            const code = responseData?.code ?? null;
            const errorMessage =
                responseData?.error || responseData?.message || error?.message || "Unexpected error.";
            const isOcrValidation =
                responseData &&
                responseData.Success === false &&
                (responseData.Expected != null || Array.isArray(responseData.Seen));
            const isTimeoutOrNetwork =
                error?.code === "ECONNABORTED" ||
                /Network Error/i.test(error?.message ?? "") ||
                status === 504 ||
                status === 502;
            if (isOcrValidation) {
                failedColorBatchRef.current = batchIndex;
                setUploadValidationError(responseData);
                setShowWrongMeasurementData(true);
                return false;
            }
            if (isTimeoutOrNetwork) {
                // measure_color may have actually completed server-side. Poll
                // patch_group status before surfacing a hard error to the user.
                setColorMeasureRecoveryBusy(true);
                const finished = await pollColorMeasureCompletion(batchIndex);
                setColorMeasureRecoveryBusy(false);
                if (finished) {
                    await getPatchDetails();
                    return true;
                }
            }
            failedColorBatchRef.current = batchIndex;
            setColorMeasureError({
                flavour: isTimeoutOrNetwork ? "timedOut" : status === 400 ? "precondition" : "server",
                status,
                code,
                message: errorMessage,
                batchIndex,
            });
            return false;
        }
    };

    /**
     * After a measure_color call apparently failed on the wire (timeout,
     * network blip, 504), the backend may already be done. Poll the
     * patch-group status endpoint so the UI can recover instead of
     * showing a misleading "OCR mismatch" dialog.
     */
    const pollColorMeasureCompletion = async (
        batchIndex,
        { attempts = 12, intervalMs = 5000 } = {}
    ) => {
        for (let i = 0; i < attempts; i++) {
            try {
                const statusResp = await axios.post("api/patch/index/detail", {
                    explorationId: explorationId,
                    iterationNumber: iterationRef.current.number,
                    isWidthPatchGroup: false,
                });
                const data = statusResp?.data ?? {};
                if (data.measured) {
                    return true;
                }
                if (Number.isFinite(data.currentIndex) && data.currentIndex > batchIndex) {
                    return true;
                }
            } catch (pollError) {
                console.warn("pollColorMeasureCompletion: status check failed", pollError);
            }
            await sleep(intervalMs);
        }
        return false;
    };

    const handleCloseColorMeasureError = () => setColorMeasureError(null);

    const handleGoToWidthReviewFromColorError = () => {
        setColorMeasureError(null);
        navigate(stepRoute("width"));
    };

    const handleRetryColorMeasure = async () => {
        const target = colorMeasureError?.batchIndex ?? 0;
        setColorMeasureError(null);
        setMeasureColorBusy(true);
        try {
            const ok = await measureColorForBatch(target, { skipIdentifierValidation: false });
            if (!ok) return;
            const slots = colorSlotsRef.current;
            for (let i = target + 1; i < slots.length; i++) {
                if (slots[i]?.serverComplete) continue;
                const okNext = await measureColorForBatch(i, { skipIdentifierValidation: false });
                if (!okNext) return;
            }
            await getPatchDetails();
        } finally {
            setMeasureColorBusy(false);
        }
    };

    const clearCameraPreviewsAndCorners = () => {
        setColorSlots([]);
        setModalSlotIndex(null);
    };

    const completeManualExploration = async (navigatePageName) => {
        setCompletingBusy(true);
        setCompleteError(null);
        try {
            await axios.post('api/next/iteration/data', { explorationId });
            clearCameraPreviewsAndCorners();
            navigate(navigatePageName);
        } catch (error) {
            console.error('Error completing the exploration:', error);
            setCompleteError(
                error?.response?.data?.error || error?.message || 'Completing the exploration failed.'
            );
        } finally {
            setCompletingBusy(false);
        }
    };

    /**
     * The iteration's colour results are persisted by `api/measure/color`
     * before we get here. The next generation is prepared from the Results
     * page: its Next posts `api/next/iteration/data`, with the population
     * the user uploaded there when one was chosen. So this page only opens
     * the results - except for a manual exploration without the optimizer,
     * which completes here.
     */
    const openResults = (navigatePageName) => {
        if (completesAfterP0) {
            completeManualExploration(navigatePageName);
            return;
        }
        clearCameraPreviewsAndCorners();
        navigate(navigatePageName);
    };


    /**
     * Cancel used to exit silently, leaving any partial color
     * measurements in the DB. Now we open the iteration-exit dialog so
     * the user has to either continue editing or explicitly discard the
     * iteration's color measurements before leaving.
     */
    const handleCancelToExplorationsList = () => {
        setCancelDialogOpen(true);
    };

    const handleCloseCancelDialog = () => {
        setCancelDialogOpen(false);
    };

    const handleDiscardAndExit = async () => {
        try {
            await revertIterationColorMeasurement(
                explorationId,
                iterationRef.current.number
            );
        } catch (error) {
            console.error("Discard color measurement failed:", error);
        }
        setCancelDialogOpen(false);
        navigate(EXPLORATION_LIST_ROUTE);
    };

    /**
     * Recovery escape hatch: the cluster image must have been wrong (or
     * the measured widths look off) and the user wants to redo the
     * cluster step. Reverts both the (in-progress) color and the
     * already-finished width measurements for this iteration, then
     * routes back to Geometric Measurement so the user can re-upload
     * fresh microscope photographs.
     */
    const handleOpenRestartClusterDialog = () => {
        setRestartClusterError(null);
        setRestartClusterDialogOpen(true);
    };

    const handleCloseRestartClusterDialog = () => {
        if (restartClusterBusy) return;
        setRestartClusterError(null);
        setRestartClusterDialogOpen(false);
    };

    const handleConfirmRestartCluster = async () => {
        setRestartClusterBusy(true);
        setRestartClusterError(null);
        try {
            await restartIteration({
                explorationId: explorationId,
                iterationNumber: iterationRef.current.number,
                hasColorWork: true,
            });
            setRestartClusterDialogOpen(false);
            navigate(stepRoute("resolution"));
        } catch (error) {
            // No width-only fallback here: a failed color revert can leave
            // the iteration rows half-rewound, and reverting only the width
            // on top of that hides the failure while the iteration counter
            // no longer matches the marked plate. Keep the dialog open and
            // show the error instead.
            console.error("Restart cluster measurement from color page failed:", error);
            setRestartClusterError(
                "The rollback failed on the server and was stopped. Reload " +
                    "the page to see the actual iteration state, then retry " +
                    "the rollback. If it keeps failing, stop here and report " +
                    "it — do not continue marking."
            );
        } finally {
            setRestartClusterBusy(false);
        }
    };

    const handleDownloadClick = async () => {
        setDownloadMarkingLoading(true);
        await sleep(3000); // Sleep for 3 seconds
        const data = {
            "explorationId": explorationId,
            "patchType": "FULL_CLUSTER",
            "iterationNumber": iterationRef.current.number,
        };
        try {
            const response = await axios.post("api/download/marking/file", data);
            setMarkingTime((current) => markingTimeFromHeaders(response.headers) || current);
            const id4 = explorationIdLast4(explorationId);
            // Match the backend's "Pn / Qn" filename convention so the
            // downloaded file matches the label printed on the physical plate.
            // Falls back to the internal iteration number only if phase is
            // somehow unknown (e.g. very stale localStorage).
            const populationCode =
                populationCodeForPhase(
                    iterationRef.current.phase,
                    iterationRef.current.display
                ) || `iterations_${iterationRef.current.number}`;
            const filename =
                getFilenameFromContentDisposition(response.headers?.["content-disposition"]) ||
                `color_patches_marking_file_${populationCode}_${id4}.json`;
            const jsonText = JSON.stringify(response.data, null, 2);
            const url = window.URL.createObjectURL(new Blob([jsonText], { type: "application/json" }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading marking file:", error);
        } finally {
            setDownloadMarkingLoading(false);
        }
    };

    // Fetches the same marking JSON as the download button, but as a Blob so
    // it can be pushed to the local laser agent without a JS JSON round-trip.
    const getMarkingBlobForLaser = async () => {
        const data = {
            explorationId: explorationId,
            patchType: "FULL_CLUSTER",
            iterationNumber: iterationRef.current.number,
        };
        const response = await axios.post("api/download/marking/file", data, {
            responseType: "blob",
        });
        const id4 = explorationIdLast4(explorationId);
        const populationCode =
            populationCodeForPhase(
                iterationRef.current.phase,
                iterationRef.current.display
            ) || `iterations_${iterationRef.current.number}`;
        const filename =
            getFilenameFromContentDisposition(response.headers?.["content-disposition"]) ||
            `color_patches_marking_file_${populationCode}_${id4}.json`;
        return { blob: response.data, filename };
    };

    const handlePreviewMarkingClick = async () => {
        setPreviewMarkingLoading(true);
        const previewWindow = window.open("", "_blank", "width=1100,height=900");
        if (previewWindow) {
            previewWindow.document.open();
            previewWindow.document.write("<p style=\"font-family:Arial,sans-serif;padding:16px;\">Generating marking preview...</p>");
            previewWindow.document.close();
        }
        const data = {
            explorationId: explorationId,
            patchType: "FULL_CLUSTER",
            iterationNumber: iterationRef.current.number,
            previewOnly: true,
        };
        try {
            const response = await axios.post("api/download/marking/file", data, {
                responseType: "blob",
            });
            const populationCodeForTitle =
                populationCodeForPhase(
                    iterationRef.current.phase,
                    iterationRef.current.display
                ) || `iteration ${iterationRef.current.number}`;
            openMarkingPreviewWindow(
                response.data,
                `Color marking preview - ${populationCodeForTitle}`,
                previewWindow
            );
        } catch (error) {
            console.error("Error previewing marking file:", error);
            if (previewWindow && !previewWindow.closed) {
                previewWindow.document.open();
                previewWindow.document.write("<p style=\"font-family:Arial,sans-serif;padding:16px;color:#b91c1c;\">Could not load marking preview.</p>");
                previewWindow.document.close();
            }
        } finally {
            setPreviewMarkingLoading(false);
        }
    };

    useEffect(() => {
        getIterationNumber();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
    }, []);

    // Detect a normalization image stored from an earlier iteration so the
    // user can reuse it without re-selecting.
    useEffect(() => {
        if (!explorationId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get("api/normalization/default", {
                    params: { explorationId },
                });
                if (!cancelled && res.data?.exists) {
                    setSharedNormOnServer(true);
                    if (res.data.extension) {
                        setSharedNormExt(String(res.data.extension).replace(/^\./, ""));
                    }
                    if (res.data.preview) {
                        setSharedNormPreview(`data:image/png;base64,${res.data.preview}`);
                    }
                }
            } catch (error) {
                console.error("Error checking stored normalization image:", error);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const modalColorSlot = modalSlotIndex != null ? colorSlots[modalSlotIndex] : null;

    const bumpModalImageLayout = () => setModalImageLayoutTick((t) => t + 1);

    useLayoutEffect(() => {
        if (modalSlotIndex == null || !modalColorSlot?.cameraColorImage) return;
        const img = modalImageRef.current;
        if (!img) return;
        const run = () => bumpModalImageLayout();
        const ro = new ResizeObserver(run);
        ro.observe(img);
        run();
        const rafOuter = requestAnimationFrame(() => requestAnimationFrame(run));
        return () => {
            cancelAnimationFrame(rafOuter);
            ro.disconnect();
        };
    }, [modalSlotIndex, modalColorSlot?.cameraColorImage]);

    useEffect(() => {
        if (modalSlotIndex == null || !modalColorSlot?.cameraColorImage) return;
        const id = window.setTimeout(() => {
            const img = modalImageRef.current;
            if (img?.naturalWidth) bumpModalImageLayout();
        }, 0);
        return () => clearTimeout(id);
    }, [modalSlotIndex, modalColorSlot?.cameraColorImage]);

    const getScaledCoordinatesForSmallImage = (batchIndex, x_a, y_a) =>
        naturalToDisplayedImageCoords(slotImageRefs.current[batchIndex], x_a, y_a);

    // Derived from the server (index/detail `measured`, mirrored into the
    // slots' serverComplete), so a refreshed or resumed page can open the
    // results of a fully measured iteration.
    const canNavigateToSummary =
        measuredAll || (colorSlots.length > 0 && colorSlots.every((s) => s.serverComplete));
    const slotReadyForMeasure = (s) =>
        Boolean(
            s.serverComplete ||
                (s.cameraColorImage &&
                    s.cameraColorImageData &&
                    s.cornerPoints.length === 4 &&
                    s.scalingFactors)
        );
    /** A normalization image is available: freshly picked or stored from before. */
    const normReady = Boolean((sharedNormPreview && sharedNormFile) || sharedNormOnServer);
    const canRunColorMeasure =
        colorSlots.length > 0 &&
        !measuredAll &&
        normReady &&
        colorSlots.every(slotReadyForMeasure);

    const nextButtonLabel = (() => {
        if (measuredAll) {
            return "View results";
        }
        if (canRunColorMeasure) {
            return colorSlots.length > 1
                ? "Measure all patch groups & Continue"
                : "Measure & Continue";
        }
        return "Next";
    })();

    const nextPageClickFunc = async (navigatePageName) => {

        if (canRunColorMeasure) {
            setMeasureColorBusy(true);
            try {
                const n = colorSlotsRef.current.length;
                for (let i = 0; i < n; i++) {
                    if (colorSlotsRef.current[i]?.serverComplete) {
                        continue;
                    }
                    const ok = await measureColorForBatch(i, { skipIdentifierValidation: false });
                    if (!ok) {
                        return;
                    }
                }
                let allMeasured = false;
                try {
                    const statusResponse = await axios.post("api/patch/index/detail", {
                        explorationId: explorationId,
                        iterationNumber: iterationRef.current.number,
                        isWidthPatchGroup: false,
                    });
                    allMeasured = Boolean(statusResponse?.data?.measured);
                    setMeasuredAll(allMeasured);
                } catch (error) {
                    console.error("Error refreshing color patch details after measure:", error);
                }
                if (allMeasured) {
                    openResults(navigatePageName);
                }
            } finally {
                setMeasureColorBusy(false);
            }
            return;
        }

        let allMeasured = canNavigateToSummary;
        try {
            const statusResponse = await axios.post("api/patch/index/detail", {
                explorationId: explorationId,
                iterationNumber: iterationRef.current.number,
                isWidthPatchGroup: false,
            });
            allMeasured = Boolean(statusResponse?.data?.measured);
            setMeasuredAll(allMeasured);
        } catch (error) {
            console.error("Error refreshing color patch details before Next:", error);
        }
        if (allMeasured) {
            openResults(navigatePageName);
        }
    };

    const nextButtonDisabled =
        !(canNavigateToSummary || canRunColorMeasure) || measureColorBusy;

    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    maxWidth: "100%",
                    mt: "2%",
                    mb: 4,
                    boxSizing: "border-box",
                }}
            >
                <ExplorationPageShell
                    title="Color measurement"
                    subtitle="Upload main and normalization images for every patch group on this page, then use Next once to run color measurement for all groups that still need it."
                    noPaper
                    stepSidebar={{
                        steps: EXPLORATION_FLOW_STEPS,
                        currentIndex: 2,
                        iterationValue: explorationIteration,
                        populationLabel: populationLabelForPhase(
                            iterationPhase,
                            explorationIteration
                        ),
                    }}
                    contentBoxSx={{
                        width: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        px: 0,
                        py: { xs: 2, md: 3 },
                    }}
                >
                    <Stack spacing={3} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, mt: 1, alignItems: "stretch" }}>
                        <Paper elevation={0} sx={stepCardSx}>
                            <Stack spacing={1.5} sx={{ width: "100%" }}>
                                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
                                    <Box sx={stepNumberCircleSx}>1</Box>
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            Mark the color patches
                                        </Typography>
                                        <MarkingOptionsBox
                                            predictedMarkingTime={markingTime}
                                            sx={{ mt: 1.5 }}
                                            remote={
                                                <LaserMarkPanel
                                                    getMarkingBlob={getMarkingBlobForLaser}
                                                    sectionTitle="Connected laser"
                                                    markingContext={{
                                                        explorationId: explorationId,
                                                        iterationNumber: iterationRef.current.number,
                                                        patchType: "FULL_CLUSTER",
                                                    }}
                                                >
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                        Marks through the laser software on this computer.
                                                    </Typography>
                                                </LaserMarkPanel>
                                            }
                                        >
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Color marking JSON for your laser software. Preview opens in a separate
                                            window.
                                        </Typography>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                disableElevation
                                                disabled={downloadMarkingLoading}
                                                startIcon={<DownloadIcon />}
                                                onClick={handleDownloadClick}
                                            >
                                                Download marking JSON
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                disabled={previewMarkingLoading}
                                                startIcon={<PreviewIcon />}
                                                onClick={handlePreviewMarkingClick}
                                            >
                                                Preview marking
                                            </Button>
                                            {downloadMarkingLoading || previewMarkingLoading ? (
                                                <CircularProgress size={20} thickness={4} aria-label="Marking file action" />
                                            ) : null}
                                        </Stack>
                                        </MarkingOptionsBox>
                                    </Box>
                                </Stack>
                            </Stack>
                        </Paper>

                        <Paper elevation={0} sx={stepCardSx}>
                            <Stack spacing={2} sx={{ width: "100%" }}>
                                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
                                    <Box sx={stepNumberCircleSx}>2</Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            Main camera & normalization images
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Choose one normalization image below — it is reused for every patch group and
                                            every iteration. Then, for each patch group, upload the main ARW/NEF plate
                                            photo.
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Box
                                    sx={{
                                        width: "100%",
                                        boxSizing: "border-box",
                                        p: 2,
                                        borderRadius: 2,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        bgcolor: "grey.50",
                                    }}
                                >
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="subtitle2" fontWeight={700}>
                                                Normalization image
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {sharedNormPreview && sharedNormFile
                                                    ? "This image will be used for every patch group in this iteration."
                                                    : sharedNormOnServer
                                                      ? "Reusing the normalization image uploaded earlier. Choose a new file only if you want to replace it."
                                                      : "Upload the flat-field reference once; it is reused for every patch group and later iterations."}
                                            </Typography>
                                            <CameraCapturePanel
                                                label="Capture normalization photo"
                                                disabled={sharedNormBusy || measureColorBusy}
                                                onCaptured={processSharedNormFile}
                                                manualUpload={
                                                    <Button
                                                        component="label"
                                                        variant="outlined"
                                                        size="small"
                                                        disableElevation
                                                        disabled={sharedNormBusy || measureColorBusy}
                                                        startIcon={<UploadFileIcon />}
                                                    >
                                                        Manual upload
                                                        <input
                                                            hidden
                                                            accept=".nef, .arw"
                                                            type="file"
                                                            disabled={sharedNormBusy || measureColorBusy}
                                                            onChange={handleSharedNormImageChange}
                                                        />
                                                    </Button>
                                                }
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                width: 160,
                                                flexShrink: 0,
                                                aspectRatio: "1",
                                                position: "relative",
                                                borderRadius: 2,
                                                overflow: "hidden",
                                                bgcolor: sharedNormPreview ? "grey.100" : "grey.50",
                                                border: "1px dashed",
                                                borderColor: sharedNormPreview ? "divider" : "text.disabled",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            {sharedNormBusy && (
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        zIndex: 4,
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: 1,
                                                        px: 1.5,
                                                        bgcolor: "rgba(255, 255, 255, 0.88)",
                                                        backdropFilter: "blur(4px)",
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary" align="center">
                                                        {sharedNormPreviewMessage || "Processing…"}
                                                    </Typography>
                                                    <LinearProgress sx={{ width: "90%" }} />
                                                </Box>
                                            )}
                                            {sharedNormPreview ? (
                                                <img
                                                    src={sharedNormPreview}
                                                    alt="Normalization preview"
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "contain",
                                                        display: "block",
                                                    }}
                                                />
                                            ) : sharedNormOnServer ? (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ px: 1.5, textAlign: "center" }}
                                                >
                                                    On file from earlier
                                                </Typography>
                                            ) : (
                                                <Typography
                                                    variant="caption"
                                                    color="text.disabled"
                                                    sx={{ px: 1.5, textAlign: "center" }}
                                                >
                                                    Preview appears here
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </Box>

                                {colorSlots.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Loading patch groups…
                                    </Typography>
                                ) : (
                                    colorSlots.map((slot, batchIndex) => (
                                        <Stack
                                            key={batchIndex}
                                            spacing={2.5}
                                            sx={{
                                                pt: batchIndex > 0 ? 2.5 : 0,
                                                borderTop: batchIndex > 0 ? "1px solid" : "none",
                                                borderColor: "divider",
                                            }}
                                        >
                                            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                                                Batch {batchIndex + 1} of {colorSlots.length}
                                            </Typography>
                                            {slot.serverComplete ? (
                                                <Alert severity="success" variant="outlined" sx={{ borderRadius: 2 }}>
                                                    Color measurement for this patch group is already recorded for this
                                                    iteration.
                                                </Alert>
                                            ) : (
                                                <>
                                                    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant="subtitle2" fontWeight={700}>
                                                                Main camera image
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                Click the preview to place or adjust the four corner points.
                                                            </Typography>
                                                            <CameraCapturePanel
                                                                label="Capture plate photo"
                                                                disabled={slot.colorImageBusy || measureColorBusy}
                                                                onCaptured={(file) =>
                                                                    processCameraColorFileForSlot(batchIndex, file)
                                                                }
                                                                manualUpload={
                                                                    <Button
                                                                        component="label"
                                                                        variant="outlined"
                                                                        size="small"
                                                                        disableElevation
                                                                        disabled={slot.colorImageBusy || measureColorBusy}
                                                                        startIcon={<UploadFileIcon />}
                                                                    >
                                                                        Manual upload
                                                                        <input
                                                                            hidden
                                                                            accept=".nef, .arw"
                                                                            type="file"
                                                                            disabled={slot.colorImageBusy || measureColorBusy}
                                                                            onChange={(e) =>
                                                                                handleCameraColorImageChangeForSlot(batchIndex, e)
                                                                            }
                                                                        />
                                                                    </Button>
                                                                }
                                                            />
                                                        </Box>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
                                                        <Stack
                                                            direction="row"
                                                            spacing={1.5}
                                                            alignItems="flex-start"
                                                            flexWrap="wrap"
                                                            sx={{ flex: 1, minWidth: 0 }}
                                                        >
                                                            <Box
                                                                role={
                                                                    slot.cameraColorImage && !slot.colorImageBusy
                                                                        ? "button"
                                                                        : undefined
                                                                }
                                                                tabIndex={
                                                                    slot.cameraColorImage && !slot.colorImageBusy ? 0 : undefined
                                                                }
                                                                aria-label={
                                                                    slot.cameraColorImage && !slot.colorImageBusy
                                                                        ? "Open full screen to adjust corner points"
                                                                        : undefined
                                                                }
                                                                onClick={
                                                                    slot.cameraColorImage && !slot.colorImageBusy
                                                                        ? () => {
                                                                              setModalSlotIndex(batchIndex);
                                                                          }
                                                                        : undefined
                                                                }
                                                                onKeyDown={
                                                                    slot.cameraColorImage && !slot.colorImageBusy
                                                                        ? (e) => {
                                                                              if (e.key === "Enter" || e.key === " ") {
                                                                                  e.preventDefault();
                                                                                  setModalSlotIndex(batchIndex);
                                                                              }
                                                                          }
                                                                        : undefined
                                                                }
                                                                sx={{
                                                                    flex: 1,
                                                                    minWidth: 0,
                                                                    maxWidth: 440,
                                                                    aspectRatio: "1",
                                                                    position: "relative",
                                                                    borderRadius: 2,
                                                                    overflow: "hidden",
                                                                    bgcolor: slot.cameraColorImage ? "grey.100" : "grey.50",
                                                                    border: "1px dashed",
                                                                    borderColor: slot.cameraColorImage ? "divider" : "text.disabled",
                                                                    ...(slot.cameraColorImage && !slot.colorImageBusy
                                                                        ? {
                                                                              cursor: "pointer",
                                                                              transition:
                                                                                  "border-color 0.15s ease, border-style 0.15s ease",
                                                                              "&:hover": {
                                                                                  borderColor: "primary.main",
                                                                                  borderStyle: "solid",
                                                                              },
                                                                          }
                                                                        : {}),
                                                                }}
                                                            >
                                                                {slot.colorImageBusy && (
                                                                    <Box
                                                                        sx={{
                                                                            position: "absolute",
                                                                            inset: 0,
                                                                            zIndex: 4,
                                                                            display: "flex",
                                                                            flexDirection: "column",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            gap: 1.5,
                                                                            px: 2,
                                                                            bgcolor: "rgba(255, 255, 255, 0.88)",
                                                                            backdropFilter: "blur(4px)",
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            color="text.secondary"
                                                                            align="center"
                                                                        >
                                                                            {slot.colorPreviewMessage || "Processing…"}
                                                                        </Typography>
                                                                        <LinearProgress sx={{ width: "min(90%, 280px)" }} />
                                                                    </Box>
                                                                )}
                                                                {slot.cameraColorImage ? (
                                                                    <Box sx={{ position: "absolute", inset: 0 }}>
                                                                        <img
                                                                            ref={(el) => {
                                                                                slotImageRefs.current[batchIndex] = el;
                                                                            }}
                                                                            src={slot.cameraColorImage}
                                                                            alt={`Camera preview group ${batchIndex + 1}`}
                                                                            style={{
                                                                                width: "100%",
                                                                                height: "100%",
                                                                                objectFit: "contain",
                                                                                display: "block",
                                                                            }}
                                                                            onLoad={() => setCameraThumbLayoutTick((t) => t + 1)}
                                                                        />
                                                                        {slot.cornerPoints.map((coordinate, index) => {
                                                                            const scaledCoordinates =
                                                                                getScaledCoordinatesForSmallImage(
                                                                                    batchIndex,
                                                                                    coordinate.x_a,
                                                                                    coordinate.y_a
                                                                                );
                                                                            return (
                                                                                <Box
                                                                                    key={index}
                                                                                    sx={{
                                                                                        position: "absolute",
                                                                                        top: scaledCoordinates.y,
                                                                                        left: scaledCoordinates.x,
                                                                                        width: "10px",
                                                                                        height: "10px",
                                                                                        borderRadius: "50%",
                                                                                        bgcolor: "error.main",
                                                                                        transform: "translate(-50%, -50%)",
                                                                                        pointerEvents: "none",
                                                                                    }}
                                                                                />
                                                                            );
                                                                        })}
                                                                    </Box>
                                                                ) : (
                                                                    <Box
                                                                        sx={{
                                                                            position: "absolute",
                                                                            inset: 0,
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            color={
                                                                                !slot.colorImageBusy && slot.colorPreviewMessage
                                                                                    ? "error"
                                                                                    : "text.disabled"
                                                                            }
                                                                            sx={{ px: 2, textAlign: "center" }}
                                                                        >
                                                                            {(!slot.colorImageBusy && slot.colorPreviewMessage) ||
                                                                                "Preview appears here after you choose a file"}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                            {slot.cameraColorImage &&
                                                            !slot.colorImageBusy &&
                                                            slot.cornerPoints.length !== 4 ? (
                                                                <Alert
                                                                    severity="error"
                                                                    variant="outlined"
                                                                    role="status"
                                                                    sx={(theme) => ({
                                                                        flex: "1 1 220px",
                                                                        maxWidth: 400,
                                                                        py: 1.25,
                                                                        px: 1.5,
                                                                        borderRadius: 2,
                                                                        alignItems: "flex-start",
                                                                        borderColor: alpha(theme.palette.error.main, 0.28),
                                                                        bgcolor: alpha(theme.palette.error.main, 0.06),
                                                                        color: "text.primary",
                                                                        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                                                                        "& .MuiAlert-icon": {
                                                                            color: theme.palette.error.main,
                                                                            opacity: 0.95,
                                                                        },
                                                                        "& .MuiAlert-message": {
                                                                            width: "100%",
                                                                            paddingTop: "2px",
                                                                        },
                                                                    })}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ fontWeight: 500, lineHeight: 1.55, m: 0 }}
                                                                    >
                                                                        Place exactly four corner points (
                                                                        {slot.cornerPoints.length} selected). Open the preview to
                                                                        adjust.
                                                                    </Typography>
                                                                </Alert>
                                                            ) : null}
                                                        </Stack>
                                                    </Stack>
                                                </>
                                            )}
                                        </Stack>
                                    ))
                                )}
                            </Stack>
                        </Paper>

                        <Divider sx={{ width: "100%" }} />

                        <Box sx={{ width: "100%", pt: 1 }}>
                            <NavBarBottom
                                variant="embedded"
                                prevPage={stepRoute("width")}
                                nextPage={stepRoute("summary")}
                                leadingSlot={
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        disableElevation
                                        sx={navBottomButtonSx}
                                        onClick={handleCancelToExplorationsList}
                                        disabled={measureColorBusy}
                                    >
                                        Cancel
                                    </Button>
                                }
                                leftSlot={
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={<ReplayIcon />}
                                        sx={navBottomButtonSx}
                                        onClick={handleOpenRestartClusterDialog}
                                        disabled={measureColorBusy || restartClusterBusy}
                                    >
                                        Restart iteration
                                    </Button>
                                }
                                enableNextPage={nextButtonDisabled || completingBusy}
                                nextPageClickFunc={nextPageClickFunc}
                                nextLoading={measureColorBusy || completingBusy}
                                nextLabel={
                                    completesAfterP0
                                        ? String(nextButtonLabel).replace(/Continue$|^Next$/, "Finish")
                                        : nextButtonLabel
                                }
                            />
                            {completeError ? (
                                <Alert severity="error" sx={{ mt: 2 }} onClose={() => setCompleteError(null)}>
                                    {completeError}
                                </Alert>
                            ) : null}
                        </Box>
                    </Stack>
                </ExplorationPageShell>
            </Box>

            {completingBusy ? <LoadComponent message="Completing the exploration..." /> : null}
            <Modal
                open={modalSlotIndex !== null}
                onClose={() => {
                    setModalSlotIndex(null);
                }}
                aria-labelledby="corner-adjust-modal-title"
                BackdropProps={{
                    sx: { backgroundColor: "rgba(0, 0, 0, 0.65)" },
                }}
            >
                <MuiBox
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "96vw", sm: "92vw" },
                        maxWidth: 1100,
                        height: "min(90vh, 920px)",
                        maxHeight: "90vh",
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        outline: "none",
                    }}
                >
                    <Typography
                        id="corner-adjust-modal-title"
                        component="h2"
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{ flexShrink: 0, px: 2, pt: 2, pb: 1 }}
                    >
                        Adjust corner points
                        {modalSlotIndex != null && colorSlots.length > 1
                            ? ` — patch group ${modalSlotIndex + 1} of ${colorSlots.length}`
                            : null}
                    </Typography>
                    {modalColorSlot?.cameraColorImage ? (
                        <Box
                            sx={{
                                position: "relative",
                                flex: 1,
                                minHeight: 0,
                                mx: 2,
                                mb: 0,
                                bgcolor: "grey.100",
                                borderRadius: 1,
                                overflow: "hidden",
                            }}
                        >
                            <Box sx={{ position: "absolute", inset: 0 }}>
                                <img
                                    ref={modalImageRef}
                                    src={modalColorSlot.cameraColorImage}
                                    alt=""
                                    onLoad={bumpModalImageLayout}
                                    onClick={handleImageClick}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        display: "block",
                                        cursor: "crosshair",
                                        margin: 0,
                                        verticalAlign: "top",
                                    }}
                                />
                                <Box
                                    key={modalImageLayoutTick}
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        pointerEvents: "none",
                                        zIndex: 1,
                                    }}
                                >
                                    {(modalColorSlot.cornerPoints ?? []).map((coordinate, index) => {
                                        const p = naturalToDisplayedImageCoords(
                                            modalImageRef.current,
                                            coordinate.x_a,
                                            coordinate.y_a
                                        );
                                        return (
                                            <Box
                                                key={index}
                                                sx={{
                                                    position: "absolute",
                                                    top: p.y,
                                                    left: p.x,
                                                    width: "10px",
                                                    height: "10px",
                                                    borderRadius: "50%",
                                                    bgcolor: "error.main",
                                                    transform: "translate(-50%, -50%)",
                                                    pointerEvents: "none",
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Box>
                    ) : null}
                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="flex-end"
                        alignItems="center"
                        flexWrap="wrap"
                        sx={{
                            flexShrink: 0,
                            px: 2,
                            py: 2,
                            borderTop: 1,
                            borderColor: "divider",
                            bgcolor: "background.paper",
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleClearCornerPoints}
                            disabled={automaticCornerDetectionBusy}
                            sx={actionButtonSx}
                        >
                            Clear corner points
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleAutomaticCornerDetection}
                            disabled={
                                automaticCornerDetectionBusy ||
                                !(modalColorSlot?.cameraColorImageData instanceof File) ||
                                !modalColorSlot?.fileExtensionColorImage
                            }
                            sx={actionButtonSx}
                        >
                            {automaticCornerDetectionBusy ? (
                                <CircularProgress size={22} thickness={4} aria-label="Detecting corners" />
                            ) : (
                                "Automatic Detection"
                            )}
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation
                            disabled={(modalColorSlot?.cornerPoints?.length ?? 0) !== 4 || automaticCornerDetectionBusy}
                            title={
                                automaticCornerDetectionBusy
                                    ? "Wait for automatic detection to finish."
                                    : (modalColorSlot?.cornerPoints?.length ?? 0) !== 4
                                      ? "Select exactly four corner points on the image before saving."
                                      : undefined
                            }
                            onClick={() => {
                                setModalSlotIndex(null);
                            }}
                            sx={actionButtonSx}
                        >
                            Save
                        </Button>
                    </Stack>
                </MuiBox>
            </Modal>

            <Dialog
                open={showWrongMeasurementData}
                onClose={handleCloseErrorDialog}
                aria-labelledby="color-upload-error-title"
                aria-describedby="color-upload-error-description"
            >
                <DialogTitle id="color-upload-error-title">Attention!</DialogTitle>
                <DialogContent>
                    <DialogContentText id="color-upload-error-description" component="div">
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            The server could not confirm the printed labels <strong>[iteration][batch]</strong> in your photo
                            (automatic check before upload). The photo must clearly show the same labels as the current
                            exploration step.
                        </Typography>
                        {uploadValidationError?.Expected != null ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Expected: iteration <strong>{uploadValidationError.Expected.iteration}</strong>, batch{" "}
                                <strong>{uploadValidationError.Expected.batch}</strong>.
                                {Array.isArray(uploadValidationError.Seen) && uploadValidationError.Seen.length > 0 ? (
                                    <>
                                        {" "}
                                        OCR read:{" "}
                                        {uploadValidationError.Seen.map((c) => c.text).filter(Boolean).join("; ") ||
                                            "(no text matched)"}
                                    </>
                                ) : (
                                    <> No label text was read from the image (lighting, angle, or crop).</>
                                )}
                            </Typography>
                        ) : null}
                        {uploadValidationError?.error ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                                {uploadValidationError.error}
                            </Typography>
                        ) : null}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 2, pb: 2 }}>
                    <Button onClick={handleCloseErrorDialog} color="inherit" sx={{ textTransform: "none" }}>
                        Close
                    </Button>
                    <Button
                        onClick={handleContinueWithoutIdentifierCheckColor}
                        variant="contained"
                        color="primary"
                        disabled={measureColorBusy}
                        sx={{ textTransform: "none" }}
                    >
                        Continue anyway — I confirm this image is correct
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(colorMeasureError)}
                onClose={handleCloseColorMeasureError}
                aria-labelledby="color-measure-error-title"
                aria-describedby="color-measure-error-description"
            >
                <DialogTitle id="color-measure-error-title">
                    {colorMeasureError?.flavour === "timedOut"
                        ? "Color measurement timed out"
                        : colorMeasureError?.flavour === "precondition"
                          ? "Color measurement rejected"
                          : "Color measurement failed"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="color-measure-error-description" component="div">
                        {colorMeasureError?.flavour === "timedOut" ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                The color measurement is taking longer than the proxy timeout. The
                                backend may still be running. Wait a moment and click "Retry" — the
                                already-uploaded image is reused.
                            </Typography>
                        ) : colorMeasureError?.flavour === "precondition" ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                The server refused the measurement because the iteration is not in
                                the expected state (typically: cluster widths are saved but the
                                FULL_CLUSTER patches haven&apos;t been generated yet). Reload from
                                the home page so the exploration routes you to the correct step.
                            </Typography>
                        ) : (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                The color measurement step did not finish successfully. You can
                                retry this batch, or reload from the home page.
                            </Typography>
                        )}
                        {colorMeasureError?.message ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                                {colorMeasureError.status ? `HTTP ${colorMeasureError.status}: ` : ""}
                                {colorMeasureError.message}
                                {colorMeasureError.code ? ` (${colorMeasureError.code})` : ""}
                            </Typography>
                        ) : null}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 2, pb: 2 }}>
                    <Button
                        onClick={handleCloseColorMeasureError}
                        color="inherit"
                        sx={{ textTransform: "none" }}
                    >
                        Close
                    </Button>
                    {colorMeasureError?.flavour === "precondition" ||
                    colorMeasureError?.code === "FULL_CLUSTER_NOT_READY" ? (
                        <Button
                            onClick={handleGoToWidthReviewFromColorError}
                            variant="contained"
                            color="primary"
                            sx={{ textTransform: "none" }}
                        >
                            Go to width review
                        </Button>
                    ) : (
                        <Button
                            onClick={handleRetryColorMeasure}
                            variant="contained"
                            color="primary"
                            disabled={measureColorBusy || colorMeasureRecoveryBusy}
                            sx={{ textTransform: "none" }}
                        >
                            {measureColorBusy || colorMeasureRecoveryBusy ? "Working…" : "Retry"}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog
                open={restartClusterDialogOpen}
                onClose={handleCloseRestartClusterDialog}
                aria-labelledby="restart-cluster-dialog-title"
                aria-describedby="restart-cluster-dialog-description"
            >
                <DialogTitle id="restart-cluster-dialog-title">
                    Restart iteration?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="restart-cluster-dialog-description">
                        This discards the cluster width measurements (and any
                        color measurements already collected) for{" "}
                        <strong>this iteration</strong> and takes you back to
                        Geometric Measurement so you can upload fresh
                        microscope photos. Use this if the cluster image you
                        uploaded earlier was wrong.
                    </DialogContentText>
                    {restartClusterError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {restartClusterError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 2, pb: 2 }}>
                    <Button
                        onClick={handleCloseRestartClusterDialog}
                        disabled={restartClusterBusy}
                        sx={{ textTransform: "none" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmRestartCluster}
                        variant="contained"
                        color="error"
                        autoFocus
                        disabled={restartClusterBusy}
                        startIcon={
                            restartClusterBusy ? (
                                <CircularProgress
                                    size={16}
                                    thickness={5}
                                    sx={{ color: "inherit" }}
                                />
                            ) : null
                        }
                        sx={{ textTransform: "none" }}
                    >
                        {restartClusterBusy ? "Reverting…" : "Restart iteration"}
                    </Button>
                </DialogActions>
            </Dialog>

            <IterationExitDialog
                open={cancelDialogOpen}
                onClose={handleCloseCancelDialog}
                onDiscardAndExit={handleDiscardAndExit}
                summary={
                    totalBatches > 0
                        ? measuredAll
                            ? `All ${totalBatches} color patch group(s) in this iteration have been measured.`
                            : `You've measured ${Math.min(
                                  currentPatchIndex,
                                  totalBatches
                              )} of ${totalBatches} color patch group(s) in this iteration.`
                        : ""
                }
                stepLabel="this iteration's color measurements"
                discardLabel="Discard color measurements and exit"
            />
        </>
    );
}
